// The Canon: the Lean Pyramid 6.0 (Jidoka edition) walked as the descent.
// Source of truth is the vault (canon/LEAN_PYRAMID_6_0.mm); this file carries
// the public reading of it: twenty-one stations down the snake path, apex to
// base, plus the lexicon of Japanese terms and the gfx labels the instrument
// drawings speak in. Copy is authored natively per locale, never translated.

const canonContent = {
  'en-US': {
    seo: {
      title: 'The Canon | Lean it 101',
      description:
        'The Lean Pyramid 6.0, Jidoka edition: the management model for companies where humans direct AI agents. Twenty-one practices, three layers, one open canon.',
    },
    hero: {
      kicker: 'The canon',
      headline: 'The doctrine behind everything we sell.',
      body: 'The Lean Pyramid 6.0, Jidoka edition: our management model for companies where humans direct AI agents. The journey starts at the top, with purpose, and winds down through twenty-one practices until value flows at the base. Walk it.',
    },
    spine: {
      apexLabel: 'Trust at the apex',
      apex: 'People get trusted. Agents get verified, and that verification protects the trust between people.',
      baseLabel: 'Value at the base',
      base: 'Everything stands on value delivered to the customer. If value stops flowing, the pyramid has nothing to stand on.',
      principles: ['Deliver value continuously', 'Create an organization safe to learn', 'Improve relentlessly'],
    },
    meta: {
      discuss: 'Bring this into your company',
    },
    climb: {
      skip: 'Skip to the base',
      loop: 'Review and improve relentlessly closes the loop, and the cycle restarts.',
    },
    gfx: {
      how: 'how',
      what: 'what',
      safety: 'SAFETY',
      ladder: ['direct', 'coach', 'support', 'delegate'],
      missionTests: 'mission tests',
      board: 'board',
      teams: 'teams',
      years: 'years',
      hours: 'hours',
      wait: 'wait',
      queue: 'queue',
      doing: 'doing 2/2',
      done: 'done',
      forecast: '85% by March',
      record: 'the record',
      standardHolds: 'the standard holds the gain',
      why: 'why',
      rgr: ['red', 'green', 'refactor'],
      andonLine: 'anyone can stop the line, agents included',
      systems: 'systems · services · agents',
    },
    layers: [
      {
        id: 'strategy',
        accent: 1,
        name: 'Strategy',
        claim: 'Human intent. Humans decide why the company exists and where it goes. No agent sets direction.',
        stations: [
          {
            title: 'Start with purpose',
            line: 'Why before methods, methods before products.',
            doctrine: 'People join causes, and a clear purpose is what holds a team through the hard times. In the agent era it works twice: a purpose written with clarity becomes the strongest instruction in an agent’s context, because agents amplify whatever we tell them. Change has layers, from tools down to mindset, and that is why a transformation starts here, never with tools.',
          },
          {
            title: 'Share principles and values',
            line: 'How we behave when nobody is watching.',
            doctrine: 'Same purpose without same principles cannot make a team. Each team writes its own working agreements, and psychological safety comes before learning, because nobody learns while hiding. The values live written in the vault, where humans and agents read the same rules.',
          },
          {
            title: 'Develop people',
            line: 'Mastery earns trust; trust earns autonomy.',
            doctrine: 'Purpose sets direction, mastery earns trust, and with trust comes the power to decide. Leadership matches the person: direct the beginner, coach the learner, delegate to the master, and the same ladder governs how much autonomy an agent receives. Every action carries who did it, human or agent, so undoing machine work never costs anyone their reputation.',
          },
          {
            title: 'Establish organizational vision',
            line: 'Where we are going, by when, and how we will know.',
            doctrine: 'Purpose says why we exist, vision says where we are going, mission says what we do to get there. The mission is verified by mission tests: executable checks that prove the how, so a failing test means we drifted from our own mission.',
          },
          {
            title: 'Develop breakthrough objectives',
            line: 'Few, bold, and verifiable.',
            doctrine: 'Choose few objectives, because when everything is a priority, nothing is. A breakthrough looks one to three years ahead: bold enough to matter, close enough that the team can prove progress. The team commits to what it can verify.',
          },
          {
            title: 'Deploy strategy',
            line: 'The board defines the what. The how belongs to everyone.',
            terms: ['Hoshin kanri', 'Catchball'],
            doctrine: 'Hoshin kanri, direction management: strategy descends through conversation, questions and adjustments come back, and both sides commit before anything starts. The strategy is also written as versioned context, so humans and agents read the same direction, and progress lives on live boards nobody compiles by hand.',
          },
        ],
      },
      {
        id: 'management',
        accent: 10,
        name: 'Management',
        claim: 'Human policy, executed by the system. People define the rules and the system runs them, so the rules are real instead of wall decoration.',
        stations: [
          {
            title: 'Break down business goals',
            line: 'From years to hours, every level with its own rhythm.',
            doctrine: 'Work descends a single ladder: strategic objectives in years, key results in quarters, products in months, deliverables in weeks, stories in days, tasks in hours. A story map lays the work over the user’s journey so every release reads as one intent, and new scope enters through breakdown, never by jumping into the middle of the flow.',
          },
          {
            title: 'Map the value stream',
            line: 'See the whole before improving the parts.',
            doctrine: 'Map the current stream with everyone around the same map, because different perspectives reveal what one person cannot see. In software the map runs: stages and policies are published and executable, and every event lands in the record, the log that boards, forecasts and audits all read from.',
          },
          {
            title: 'Optimize the development flow',
            line: 'Pull, small batches, nothing half-open.',
            doctrine: 'Demand enters shaped, through one queue that never overloads the flow. Policies are written and visible, people pull work when ready, agents wait their turn, and the surgery rule holds: never open work you cannot close. A story proves itself done through an executable confirmation.',
          },
          {
            title: 'Manage flow through economics',
            line: 'First the work that costs most to delay.',
            doctrine: 'Cost of delay is the value lost per week while an item waits; divide it by duration and the queue orders itself. The economics moved: writing code got cheap, unverified work got expensive, so the money question is how fast verified value reaches the customer.',
          },
          {
            title: 'Understand your capacity',
            line: 'A full calendar is already a late calendar.',
            doctrine: 'Queues explode long before utilization reaches one hundred percent, so a team that knows its capacity can simply decide to never overload it. Forecasts come from the record: Little’s law first, then Monte Carlo runs over your own history, giving dates with probabilities instead of a single guess. The record measures work, never people.',
          },
          {
            title: 'Make it visual',
            line: 'What is visible gets managed.',
            terms: ['Mieruka', 'Obeya'],
            doctrine: 'When the work is visible, the problems are visible. Our obeya is virtual: the board renders from the record of events and nobody updates it by hand. Charts tell the truth, real ranges and real gaps, and refuse to draw a zero just to look complete.',
          },
          {
            title: 'Review and improve relentlessly',
            line: 'The stream carries status. Reviews exist for judgment.',
            doctrine: 'Monthly strategic, weekly tactical, daily operational: shorter cycles bring more feedback. Reviews spend no time on status, because the stream already carries it; they exist for causes, decisions and adjustments. Improvement is kaizen plus kaikaku, continuous and discontinuous, together and without pause.',
          },
          {
            title: 'Keep living standards: the vault',
            line: 'The board says where work stands. The vault says how work is done.',
            doctrine: 'The vault is the team’s versioned library of standards: playbooks, policies, decisions. The team authors it, changes are proposed and reviewed, and there is no owner caste. Agents follow the vault literally, so write it well; a kaizen is a change to the vault, and proven patterns spread as playbooks.',
          },
        ],
      },
      {
        id: 'engineering',
        accent: 19,
        name: 'Engineering',
        claim: 'Human craft, agent labor. Humans own the judgment; agents do the heavy lifting under human direction.',
        stations: [
          {
            title: 'Architecture just in time',
            line: 'The human is the architect, the captain of the ship.',
            doctrine: 'System design and architectural judgment stay with people. Architecture emerges as the system needs it: no big design up front, and no design at all is even worse. Clean seams driven by domain help humans reason and help agents work without breaking the neighbors.',
          },
          {
            title: 'Code to value',
            line: 'No code without clearly expected value.',
            doctrine: 'Rule one: every item carries its why before anyone writes a line. Rule two: code must keep earning its place, and when measured value no longer covers cost, the code is removed. Usage tracking feeds that audit.',
          },
          {
            title: 'Keep the code clean',
            line: 'Hygiene is not optional, whatever the deadline.',
            doctrine: 'Like a doctor washing hands before surgery: you cannot skip hygiene and expect a healthy patient. The best design is the simplest one that works, code without tests is legacy code, and an agent that writes coupled, debt-filled code gets rejected like anyone else. Debt is filed with its reason and managed with rigor.',
          },
          {
            title: 'Test first, verify continuously',
            line: 'The speed we can verify is the speed we can deliver.',
            doctrine: 'The spec arrives from management and engineering turns it into executable tests, so the spec proves itself. Red, green, refactor for humans; for agents, every line of work is verified before it counts. Validation became the bottleneck, which makes verification the real capacity of the system.',
          },
          {
            title: 'Automate the path to production',
            line: 'Every change walks the same automated road.',
            doctrine: 'Everyone integrates to the shared trunk several times a day, every integration builds and tests from scratch, and a failed build stops the line. Security enters with the first commit, environments are code you can rebuild with one click, and new behavior ships behind toggles so deploy and release become separate decisions.',
          },
          {
            title: 'Direct the agents',
            line: 'The human may never touch the code and is still the maestro.',
            doctrine: 'The agent receives the spec, the boundaries and the standards before it generates anything, and nothing merges unverified, without exception. Pairing extends to human plus agent: one works while the other reviews and thinks ahead. A context window is real capacity; protect it like any other, because overload produces bad work in machines too.',
          },
          {
            title: 'Monitor continuously',
            line: 'The system tells you how it feels in production.',
            doctrine: 'Observability covers analytics, heartbeat and health, and the agents are part of the system: verification pass rates, drift and cost per item get watched like the rest. Monitoring watches systems, services and agents. It is never pointed at a person.',
          },
        ],
      },
    ],
    lexicon: {
      kicker: 'The lexicon',
      title: 'Sixteen words the model thinks in.',
      hint: 'Tap a card to turn it.',
      terms: [
        { term: 'Jidoka', gloss: 'automation with a human touch', def: 'The system detects its own abnormality and stops itself, so defects never flow downstream. The word that names this edition.' },
        { term: 'Kaizen', gloss: 'continuous improvement', def: 'Many small steps, each one a PDCA cycle, climbing from the current condition toward the vision.' },
        { term: 'Kaikaku', gloss: 'discontinuous improvement', def: 'The leap onto a new curve when the old one flattens. With kaizen, it makes improvement relentless.' },
        { term: 'Muda', gloss: 'waste', def: 'Everything that adds no value to the customer. Directing agents added new wastes: burned context windows, agent rework, unverified output.' },
        { term: 'Muri', gloss: 'overload', def: 'Queues explode long before full utilization. Never overload people, systems, or context windows.' },
        { term: 'Mura', gloss: 'unevenness', def: 'Irregular flow breeds defects and failure demand. Level the flow, including the way agents claim work.' },
        { term: 'Gemba', gloss: 'the real place', def: 'Where value is actually created. Go and see. An agent’s gemba is the trace of what it did; replaying it is how you go and see machine work.' },
        { term: 'Andon', gloss: 'the stop cord', def: 'Anyone can stop the line when something is wrong. Agents pull it too, and the reason stays recorded and visible.' },
        { term: 'Poka-yoke', gloss: 'mistake proofing', def: 'Built into the process, so the error becomes impossible instead of corrected later.' },
        { term: 'Heijunka', gloss: 'leveling', def: 'Work arrives evenly instead of in destructive waves, for people and for agents alike.' },
        { term: 'Hansei', gloss: 'reflection', def: 'What did we ourselves contribute to the failure? Reflection comes before pointing at anyone else.' },
        { term: 'Kentou', gloss: 'study before building', def: 'Design the solution, assemble the context, and only then let the work begin.' },
        { term: 'Yokoten', gloss: 'spread sideways', def: 'A practice proven in one place serves every place, carried as playbooks in the vault.' },
        { term: 'Obeya', gloss: 'the big room', def: 'All the information about the work, together in one place. Ours is virtual and renders from the record.' },
        { term: 'Kamishibai', gloss: 'mini audits', def: 'Short, frequent checks that the standard is still followed. Drift is silent, so you look on a rhythm; many checks now run as code.' },
        { term: 'Hoshin kanri', gloss: 'direction management', def: 'Strategic goals drive action at every level, aligned through catchball conversation before anyone commits.' },
      ],
    },
    manual: {
      kicker: 'The field manual',
      title: 'The whole canon, bound.',
      body: 'The book in progress turns these cards into a field manual, with the stories from the floor behind every practice. Leave your email and the first chapters find you before anyone else.',
      emailPlaceholder: 'you@company.com',
      cta: 'Reserve my copy',
      sending: 'Reserving...',
      done: 'You are on the list. The first chapters will find you.',
      error: 'That did not go through. Try again, or write to contact@leanit101.com.',
    },
  },

  'pt-BR': {
    seo: {
      title: 'O Cânone | Lean it 101',
      description:
        'A Lean Pyramid 6.0, edição Jidoka: o modelo de gestão para empresas onde humanos dirigem agentes de IA. Vinte e uma práticas, três camadas, um cânone aberto.',
    },
    hero: {
      kicker: 'O cânone',
      headline: 'A doutrina por trás de tudo que vendemos.',
      body: 'A Lean Pyramid 6.0, edição Jidoka: nosso modelo de gestão para empresas onde humanos dirigem agentes de IA. A jornada começa no topo, pelo propósito, e desce por vinte e uma práticas até o valor fluir na base. Percorra.',
    },
    spine: {
      apexLabel: 'Confiança no topo',
      apex: 'Pessoas recebem confiança. Agentes recebem verificação, e essa verificação protege a confiança entre as pessoas.',
      baseLabel: 'Valor na base',
      base: 'Tudo se apoia no valor entregue ao cliente. Se o valor para de fluir, a pirâmide não tem onde se apoiar.',
      principles: ['Entregar valor continuamente', 'Criar uma organização segura para aprender', 'Melhorar sem trégua'],
    },
    meta: {
      discuss: 'Levar isso para a sua empresa',
    },
    climb: {
      skip: 'Pular para a base',
      loop: 'Revisar e melhorar sem trégua fecha o ciclo, e a jornada recomeça.',
    },
    gfx: {
      how: 'como',
      what: 'o quê',
      safety: 'SEGURANÇA',
      ladder: ['dirigir', 'treinar', 'apoiar', 'delegar'],
      missionTests: 'testes de missão',
      board: 'direção',
      teams: 'times',
      years: 'anos',
      hours: 'horas',
      wait: 'espera',
      queue: 'fila',
      doing: 'fazendo 2/2',
      done: 'feito',
      forecast: '85% até março',
      record: 'o registro',
      standardHolds: 'o padrão segura o ganho',
      why: 'porquê',
      rgr: ['vermelho', 'verde', 'refatora'],
      andonLine: 'qualquer um para a linha, agentes inclusive',
      systems: 'sistemas · serviços · agentes',
    },
    layers: [
      {
        id: 'strategy',
        accent: 1,
        name: 'Estratégia',
        claim: 'Intenção humana. Humanos decidem por que a empresa existe e para onde ela vai. Nenhum agente define direção.',
        stations: [
          {
            title: 'Comece pelo propósito',
            line: 'O porquê antes dos métodos, os métodos antes dos produtos.',
            doctrine: 'Pessoas se juntam a causas, e um propósito claro é o que segura um time nos momentos difíceis. Na era dos agentes ele vale em dobro: um propósito escrito com clareza vira a instrução mais forte no contexto de um agente, porque agentes amplificam o que dizemos a eles. A mudança tem camadas, das ferramentas até o mindset, e é por isso que uma transformação começa aqui, nunca pelas ferramentas.',
          },
          {
            title: 'Compartilhe princípios e valores',
            line: 'Como nos comportamos quando ninguém está olhando.',
            doctrine: 'Mesmo propósito sem os mesmos princípios não faz um time. Cada time escreve seus próprios acordos de trabalho, e segurança psicológica vem antes do aprendizado, porque ninguém aprende se escondendo. Os valores vivem escritos no vault, onde humanos e agentes leem as mesmas regras.',
          },
          {
            title: 'Desenvolva pessoas',
            line: 'Maestria conquista confiança; confiança conquista autonomia.',
            doctrine: 'O propósito dá a direção, a maestria conquista confiança, e com confiança vem o poder de decidir. A liderança se ajusta à pessoa: dirija o iniciante, treine o aprendiz, delegue ao mestre, e a mesma escada governa quanta autonomia um agente recebe. Toda ação carrega quem a fez, humano ou agente, então desfazer trabalho de máquina não custa a reputação de ninguém.',
          },
          {
            title: 'Estabeleça a visão organizacional',
            line: 'Para onde vamos, até quando, e como saberemos.',
            doctrine: 'O propósito diz por que existimos, a visão diz aonde vamos, a missão diz o que fazemos para chegar lá. A missão é verificada por testes de missão: checagens executáveis que provam o como, então um teste falhando significa que derivamos da nossa própria missão.',
          },
          {
            title: 'Desenvolva objetivos de ruptura',
            line: 'Poucos, ousados e verificáveis.',
            doctrine: 'Escolha poucos objetivos, porque quando tudo é prioridade, nada é. Uma ruptura olha de um a três anos à frente: ousada o bastante para importar, próxima o bastante para o time provar progresso. O time se compromete com o que consegue verificar.',
          },
          {
            title: 'Desdobre a estratégia',
            line: 'A direção define o quê. O como pertence a todos.',
            terms: ['Hoshin kanri', 'Catchball'],
            doctrine: 'Hoshin kanri, gestão da direção: a estratégia desce por conversa, perguntas e ajustes voltam, e os dois lados se comprometem antes de qualquer coisa começar. A estratégia também é escrita como contexto versionado, para humanos e agentes lerem a mesma direção, e o progresso vive em painéis vivos que ninguém compila à mão.',
          },
        ],
      },
      {
        id: 'management',
        accent: 10,
        name: 'Gestão',
        claim: 'Política humana, executada pelo sistema. Pessoas definem as regras e o sistema as roda, para que as regras sejam reais em vez de decoração de parede.',
        stations: [
          {
            title: 'Quebre os objetivos de negócio',
            line: 'De anos a horas, cada nível com seu ritmo.',
            doctrine: 'O trabalho desce uma escada única: objetivos estratégicos em anos, key results em trimestres, produtos em meses, entregáveis em semanas, histórias em dias, tarefas em horas. Um story map deita o trabalho sobre a jornada do usuário para cada release ler como uma intenção só, e escopo novo entra pela quebra, nunca pulando no meio do fluxo.',
          },
          {
            title: 'Mapeie o fluxo de valor',
            line: 'Veja o todo antes de melhorar as partes.',
            doctrine: 'Mapeie o fluxo atual com todos em volta do mesmo mapa, porque perspectivas diferentes revelam o que uma pessoa sozinha não vê. Em software o mapa roda: estágios e políticas são publicados e executáveis, e cada evento cai no registro, o log de onde painéis, previsões e auditorias leem.',
          },
          {
            title: 'Otimize o fluxo de desenvolvimento',
            line: 'Pull, lotes pequenos, nada meio aberto.',
            doctrine: 'A demanda entra moldada, por uma fila única que nunca sobrecarrega o fluxo. As políticas são escritas e visíveis, pessoas puxam trabalho quando estão prontas, agentes esperam a vez, e a regra da cirurgia vale: nunca abra um trabalho que você não consegue fechar. Uma história prova que está pronta por uma confirmação executável.',
          },
          {
            title: 'Gerencie o fluxo pela economia',
            line: 'Primeiro o trabalho que custa mais caro esperar.',
            doctrine: 'Custo de atraso é o valor perdido por semana enquanto um item espera; divida pela duração e a fila se ordena sozinha. A economia mudou: escrever código ficou barato, trabalho não verificado ficou caro, então a pergunta financeira é quão rápido valor verificado chega ao cliente.',
          },
          {
            title: 'Conheça a sua capacidade',
            line: 'Agenda cheia já é agenda atrasada.',
            doctrine: 'Filas explodem muito antes da utilização chegar a cem por cento, então um time que conhece sua capacidade pode simplesmente decidir nunca sobrecarregá-la. Previsões vêm do registro: lei de Little primeiro, depois Monte Carlo sobre a sua própria história, dando datas com probabilidades em vez de um palpite único. O registro mede trabalho, nunca pessoas.',
          },
          {
            title: 'Torne tudo visual',
            line: 'O que é visível é gerenciado.',
            terms: ['Mieruka', 'Obeya'],
            doctrine: 'Quando o trabalho é visível, os problemas são visíveis. Nossa obeya é virtual: o quadro renderiza do registro de eventos e ninguém o atualiza à mão. Gráficos dizem a verdade, faixas reais e lacunas reais, e se recusam a desenhar um zero só para parecer completo.',
          },
          {
            title: 'Revise e melhore sem trégua',
            line: 'O fluxo carrega o status. Reviews existem para julgamento.',
            doctrine: 'Mensal estratégico, semanal tático, diário operacional: ciclos mais curtos trazem mais feedback. Reviews não gastam tempo com status, porque o fluxo já o carrega; existem para causas, decisões e ajustes. Melhoria é kaizen mais kaikaku, contínua e descontínua, juntas e sem pausa.',
          },
          {
            title: 'Mantenha padrões vivos: o vault',
            line: 'O quadro diz onde o trabalho está. O vault diz como o trabalho é feito.',
            doctrine: 'O vault é a biblioteca versionada de padrões do time: playbooks, políticas, decisões. O time é o autor, mudanças são propostas e revisadas, e não existe casta de donos. Agentes seguem o vault ao pé da letra, então escreva bem; um kaizen é uma mudança no vault, e padrões provados se espalham como playbooks.',
          },
        ],
      },
      {
        id: 'engineering',
        accent: 19,
        name: 'Engenharia',
        claim: 'Ofício humano, trabalho de agente. Humanos são donos do julgamento; agentes fazem o trabalho pesado sob direção humana.',
        stations: [
          {
            title: 'Arquitetura just in time',
            line: 'O humano é o arquiteto, o capitão do navio.',
            doctrine: 'O desenho do sistema e o julgamento arquitetural ficam com pessoas. A arquitetura emerge conforme o sistema precisa: nada de big design antecipado, e nenhum design é ainda pior. Costuras limpas guiadas pelo domínio ajudam humanos a raciocinar e ajudam agentes a trabalhar sem quebrar os vizinhos.',
          },
          {
            title: 'Código a valor',
            line: 'Nenhum código sem valor claramente esperado.',
            doctrine: 'Regra um: todo item carrega seu porquê antes de alguém escrever uma linha. Regra dois: o código precisa continuar merecendo o lugar, e quando o valor medido não cobre mais o custo, o código é removido. O rastreio de uso alimenta essa auditoria.',
          },
          {
            title: 'Mantenha o código limpo',
            line: 'Higiene não é opcional, seja qual for o prazo.',
            doctrine: 'Como um médico lavando as mãos antes da cirurgia: não dá para pular a higiene e esperar um paciente saudável. O melhor design é o mais simples que funciona, código sem teste é código legado, e um agente que escreve código acoplado e endividado é rejeitado como qualquer um. Dívida é registrada com sua razão e gerida com rigor.',
          },
          {
            title: 'Teste primeiro, verifique sempre',
            line: 'A velocidade de verificar é a velocidade de entregar.',
            doctrine: 'A especificação chega da gestão e a engenharia a transforma em testes executáveis, para a spec provar a si mesma. Vermelho, verde, refatora para humanos; para agentes, cada linha de trabalho é verificada antes de contar. A validação virou o gargalo, o que faz da verificação a capacidade real do sistema.',
          },
          {
            title: 'Automatize o caminho até produção',
            line: 'Toda mudança percorre a mesma estrada automatizada.',
            doctrine: 'Todos integram ao tronco compartilhado várias vezes por dia, cada integração compila e testa do zero, e um build quebrado para a linha. A segurança entra no primeiro commit, os ambientes são código que se reconstrói com um clique, e comportamento novo sai atrás de toggles, separando deploy de release.',
          },
          {
            title: 'Dirija os agentes',
            line: 'O humano pode nunca tocar o código e continua sendo o maestro.',
            doctrine: 'O agente recebe a spec, as fronteiras e os padrões antes de gerar qualquer coisa, e nada mergeia sem verificação, sem exceção. O pareamento se estende a humano mais agente: um trabalha enquanto o outro revisa e pensa à frente. Janela de contexto é capacidade real; proteja como qualquer outra, porque sobrecarga produz trabalho ruim em máquinas também.',
          },
          {
            title: 'Monitore continuamente',
            line: 'O sistema conta como se sente em produção.',
            doctrine: 'Observabilidade cobre analytics, batimento e saúde, e os agentes fazem parte do sistema: taxa de verificação, deriva e custo por item são vigiados como o resto. O monitoramento observa sistemas, serviços e agentes. Nunca aponta para uma pessoa.',
          },
        ],
      },
    ],
    lexicon: {
      kicker: 'O léxico',
      title: 'Dezesseis palavras em que o modelo pensa.',
      hint: 'Toque num cartão para virar.',
      terms: [
        { term: 'Jidoka', gloss: 'automação com toque humano', def: 'O sistema detecta a própria anormalidade e para sozinho, para defeito nenhum fluir adiante. A palavra que dá nome a esta edição.' },
        { term: 'Kaizen', gloss: 'melhoria contínua', def: 'Muitos passos pequenos, cada um um ciclo PDCA, subindo da condição atual em direção à visão.' },
        { term: 'Kaikaku', gloss: 'melhoria descontínua', def: 'O salto para uma curva nova quando a antiga achata. Com o kaizen, faz a melhoria ser incansável.' },
        { term: 'Muda', gloss: 'desperdício', def: 'Tudo que não agrega valor ao cliente. Dirigir agentes criou desperdícios novos: janelas de contexto queimadas, retrabalho de agente, saída não verificada.' },
        { term: 'Muri', gloss: 'sobrecarga', def: 'Filas explodem muito antes da utilização total. Nunca sobrecarregue pessoas, sistemas ou janelas de contexto.' },
        { term: 'Mura', gloss: 'desnivelamento', def: 'Fluxo irregular gera defeitos e demanda de falha. Nivele o fluxo, inclusive o jeito como agentes puxam trabalho.' },
        { term: 'Gemba', gloss: 'o lugar real', def: 'Onde o valor é realmente criado. Vá e veja. O gemba de um agente é o rastro do que ele fez; reproduzi-lo é como se vê trabalho de máquina.' },
        { term: 'Andon', gloss: 'o cordão de parada', def: 'Qualquer um pode parar a linha quando algo está errado. Agentes também puxam, e a razão fica registrada e visível.' },
        { term: 'Poka-yoke', gloss: 'à prova de erro', def: 'Construído dentro do processo, para o erro se tornar impossível em vez de corrigido depois.' },
        { term: 'Heijunka', gloss: 'nivelamento', def: 'O trabalho chega por igual em vez de em ondas destrutivas, para pessoas e para agentes.' },
        { term: 'Hansei', gloss: 'reflexão', def: 'O que nós mesmos contribuímos para a falha? A reflexão vem antes de apontar para qualquer outro.' },
        { term: 'Kentou', gloss: 'estudar antes de construir', def: 'Desenhe a solução, monte o contexto, e só então deixe o trabalho começar.' },
        { term: 'Yokoten', gloss: 'espalhar para os lados', def: 'Uma prática provada num lugar serve a todos os lugares, carregada como playbooks no vault.' },
        { term: 'Obeya', gloss: 'a sala grande', def: 'Toda a informação sobre o trabalho, junta num lugar só. A nossa é virtual e renderiza do registro.' },
        { term: 'Kamishibai', gloss: 'mini auditorias', def: 'Checagens curtas e frequentes de que o padrão ainda é seguido. A deriva é silenciosa, então se olha num ritmo; muitas já rodam como código.' },
        { term: 'Hoshin kanri', gloss: 'gestão da direção', def: 'Os objetivos estratégicos dirigem a ação em todos os níveis, alinhados por catchball antes de qualquer compromisso.' },
      ],
    },
    manual: {
      kicker: 'O manual de campo',
      title: 'O cânone inteiro, encadernado.',
      body: 'O livro em andamento transforma estes cartões em um manual de campo, com as histórias de chão de fábrica por trás de cada prática. Deixe seu email e os primeiros capítulos te encontram antes de todo mundo.',
      emailPlaceholder: 'voce@empresa.com',
      cta: 'Reservar minha cópia',
      sending: 'Reservando...',
      done: 'Você está na lista. Os primeiros capítulos vão te encontrar.',
      error: 'Não foi dessa vez. Tente de novo, ou escreva para contact@leanit101.com.',
    },
  },

  es: {
    seo: {
      title: 'El Canon | Lean it 101',
      description:
        'La Lean Pyramid 6.0, edición Jidoka: el modelo de gestión para empresas donde humanos dirigen agentes de IA. Veintiuna prácticas, tres capas, un canon abierto.',
    },
    hero: {
      kicker: 'El canon',
      headline: 'La doctrina detrás de todo lo que vendemos.',
      body: 'La Lean Pyramid 6.0, edición Jidoka: nuestro modelo de gestión para empresas donde humanos dirigen agentes de IA. El viaje empieza en la cima, por el propósito, y baja por veintiuna prácticas hasta que el valor fluye en la base. Recórrelo.',
    },
    spine: {
      apexLabel: 'Confianza en la cima',
      apex: 'Las personas reciben confianza. Los agentes reciben verificación, y esa verificación protege la confianza entre las personas.',
      baseLabel: 'Valor en la base',
      base: 'Todo se apoya en el valor entregado al cliente. Si el valor deja de fluir, la pirámide no tiene dónde apoyarse.',
      principles: ['Entregar valor continuamente', 'Crear una organización segura para aprender', 'Mejorar sin descanso'],
    },
    meta: {
      discuss: 'Llevar esto a tu empresa',
    },
    climb: {
      skip: 'Saltar a la base',
      loop: 'Revisar y mejorar sin descanso cierra el ciclo, y el viaje vuelve a empezar.',
    },
    gfx: {
      how: 'cómo',
      what: 'qué',
      safety: 'SEGURIDAD',
      ladder: ['dirigir', 'entrenar', 'apoyar', 'delegar'],
      missionTests: 'tests de misión',
      board: 'dirección',
      teams: 'equipos',
      years: 'años',
      hours: 'horas',
      wait: 'espera',
      queue: 'cola',
      doing: 'haciendo 2/2',
      done: 'hecho',
      forecast: '85% para marzo',
      record: 'el registro',
      standardHolds: 'el estándar sostiene la ganancia',
      why: 'porqué',
      rgr: ['rojo', 'verde', 'refactor'],
      andonLine: 'cualquiera para la línea, agentes incluidos',
      systems: 'sistemas · servicios · agentes',
    },
    layers: [
      {
        id: 'strategy',
        accent: 1,
        name: 'Estrategia',
        claim: 'Intención humana. Los humanos deciden por qué existe la empresa y hacia dónde va. Ningún agente define dirección.',
        stations: [
          {
            title: 'Empieza por el propósito',
            line: 'El porqué antes de los métodos, los métodos antes de los productos.',
            doctrine: 'Las personas se unen a causas, y un propósito claro es lo que sostiene a un equipo en los momentos duros. En la era de los agentes vale doble: un propósito escrito con claridad se vuelve la instrucción más fuerte en el contexto de un agente, porque los agentes amplifican lo que les decimos. El cambio tiene capas, de las herramientas al mindset, y por eso una transformación empieza aquí, nunca por las herramientas.',
          },
          {
            title: 'Comparte principios y valores',
            line: 'Cómo nos comportamos cuando nadie mira.',
            doctrine: 'El mismo propósito sin los mismos principios no hace un equipo. Cada equipo escribe sus propios acuerdos de trabajo, y la seguridad psicológica viene antes del aprendizaje, porque nadie aprende escondiéndose. Los valores viven escritos en el vault, donde humanos y agentes leen las mismas reglas.',
          },
          {
            title: 'Desarrolla personas',
            line: 'La maestría gana confianza; la confianza gana autonomía.',
            doctrine: 'El propósito marca la dirección, la maestría gana confianza, y con la confianza llega el poder de decidir. El liderazgo se ajusta a la persona: dirige al principiante, entrena al aprendiz, delega en el maestro, y la misma escalera gobierna cuánta autonomía recibe un agente. Cada acción lleva quién la hizo, humano o agente, así que deshacer trabajo de máquina no le cuesta la reputación a nadie.',
          },
          {
            title: 'Establece la visión organizacional',
            line: 'Hacia dónde vamos, para cuándo, y cómo lo sabremos.',
            doctrine: 'El propósito dice por qué existimos, la visión dice hacia dónde vamos, la misión dice qué hacemos para llegar. La misión se verifica con tests de misión: chequeos ejecutables que prueban el cómo, así que un test fallando significa que derivamos de nuestra propia misión.',
          },
          {
            title: 'Desarrolla objetivos de ruptura',
            line: 'Pocos, audaces y verificables.',
            doctrine: 'Elige pocos objetivos, porque cuando todo es prioridad, nada lo es. Una ruptura mira de uno a tres años adelante: audaz para importar, cercana para que el equipo pruebe el progreso. El equipo se compromete con lo que puede verificar.',
          },
          {
            title: 'Despliega la estrategia',
            line: 'La dirección define el qué. El cómo pertenece a todos.',
            terms: ['Hoshin kanri', 'Catchball'],
            doctrine: 'Hoshin kanri, gestión de la dirección: la estrategia baja por conversación, las preguntas y ajustes vuelven, y ambos lados se comprometen antes de que algo empiece. La estrategia también se escribe como contexto versionado, para que humanos y agentes lean la misma dirección, y el progreso vive en tableros vivos que nadie compila a mano.',
          },
        ],
      },
      {
        id: 'management',
        accent: 10,
        name: 'Gestión',
        claim: 'Política humana, ejecutada por el sistema. Las personas definen las reglas y el sistema las corre, para que las reglas sean reales y no decoración de pared.',
        stations: [
          {
            title: 'Desglosa los objetivos de negocio',
            line: 'De años a horas, cada nivel con su ritmo.',
            doctrine: 'El trabajo baja una sola escalera: objetivos estratégicos en años, key results en trimestres, productos en meses, entregables en semanas, historias en días, tareas en horas. Un story map tiende el trabajo sobre el viaje del usuario para que cada release se lea como una sola intención, y el alcance nuevo entra por el desglose, nunca saltando al medio del flujo.',
          },
          {
            title: 'Mapea el flujo de valor',
            line: 'Ve el todo antes de mejorar las partes.',
            doctrine: 'Mapea el flujo actual con todos alrededor del mismo mapa, porque perspectivas distintas revelan lo que una persona sola no ve. En software el mapa corre: etapas y políticas se publican y ejecutan, y cada evento cae en el registro, el log del que leen tableros, pronósticos y auditorías.',
          },
          {
            title: 'Optimiza el flujo de desarrollo',
            line: 'Pull, lotes pequeños, nada a medio abrir.',
            doctrine: 'La demanda entra moldeada, por una sola cola que nunca sobrecarga el flujo. Las políticas están escritas y visibles, las personas jalan trabajo cuando están listas, los agentes esperan su turno, y la regla de la cirugía vale: nunca abras un trabajo que no puedas cerrar. Una historia prueba que está lista con una confirmación ejecutable.',
          },
          {
            title: 'Gestiona el flujo por la economía',
            line: 'Primero el trabajo que más cuesta demorar.',
            doctrine: 'El costo de demora es el valor perdido por semana mientras un ítem espera; divídelo por la duración y la cola se ordena sola. La economía cambió: escribir código se volvió barato, el trabajo sin verificar se volvió caro, así que la pregunta financiera es qué tan rápido llega valor verificado al cliente.',
          },
          {
            title: 'Conoce tu capacidad',
            line: 'Una agenda llena ya es una agenda atrasada.',
            doctrine: 'Las colas explotan mucho antes de que la utilización llegue al cien por ciento, así que un equipo que conoce su capacidad puede simplemente decidir no sobrecargarla nunca. Los pronósticos salen del registro: ley de Little primero, luego Monte Carlo sobre tu propia historia, dando fechas con probabilidades en vez de una sola conjetura. El registro mide trabajo, nunca personas.',
          },
          {
            title: 'Hazlo todo visual',
            line: 'Lo que es visible se gestiona.',
            terms: ['Mieruka', 'Obeya'],
            doctrine: 'Cuando el trabajo es visible, los problemas son visibles. Nuestra obeya es virtual: el tablero se renderiza del registro de eventos y nadie lo actualiza a mano. Los gráficos dicen la verdad, rangos reales y huecos reales, y se niegan a dibujar un cero solo para verse completos.',
          },
          {
            title: 'Revisa y mejora sin descanso',
            line: 'El flujo lleva el estado. Las reviews existen para el juicio.',
            doctrine: 'Mensual estratégica, semanal táctica, diaria operativa: ciclos más cortos traen más feedback. Las reviews no gastan tiempo en estado, porque el flujo ya lo lleva; existen para causas, decisiones y ajustes. La mejora es kaizen más kaikaku, continua y discontinua, juntas y sin pausa.',
          },
          {
            title: 'Mantén estándares vivos: el vault',
            line: 'El tablero dice dónde está el trabajo. El vault dice cómo se hace.',
            doctrine: 'El vault es la biblioteca versionada de estándares del equipo: playbooks, políticas, decisiones. El equipo es el autor, los cambios se proponen y revisan, y no existe casta de dueños. Los agentes siguen el vault al pie de la letra, así que escríbelo bien; un kaizen es un cambio al vault, y los patrones probados se esparcen como playbooks.',
          },
        ],
      },
      {
        id: 'engineering',
        accent: 19,
        name: 'Ingeniería',
        claim: 'Oficio humano, labor de agente. Los humanos son dueños del juicio; los agentes hacen el trabajo pesado bajo dirección humana.',
        stations: [
          {
            title: 'Arquitectura just in time',
            line: 'El humano es el arquitecto, el capitán del barco.',
            doctrine: 'El diseño del sistema y el juicio arquitectónico quedan con las personas. La arquitectura emerge cuando el sistema la necesita: nada de gran diseño anticipado, y ningún diseño es aún peor. Costuras limpias guiadas por el dominio ayudan a los humanos a razonar y a los agentes a trabajar sin romper a los vecinos.',
          },
          {
            title: 'Código a valor',
            line: 'Ningún código sin valor claramente esperado.',
            doctrine: 'Regla uno: cada ítem lleva su porqué antes de que alguien escriba una línea. Regla dos: el código debe seguir mereciendo su lugar, y cuando el valor medido ya no cubre el costo, el código se elimina. El rastreo de uso alimenta esa auditoría.',
          },
          {
            title: 'Mantén el código limpio',
            line: 'La higiene no es opcional, sea cual sea el plazo.',
            doctrine: 'Como un médico lavándose las manos antes de la cirugía: no puedes saltarte la higiene y esperar un paciente sano. El mejor diseño es el más simple que funciona, el código sin tests es código legado, y un agente que escribe código acoplado y endeudado es rechazado como cualquiera. La deuda se registra con su razón y se gestiona con rigor.',
          },
          {
            title: 'Testea primero, verifica siempre',
            line: 'La velocidad de verificar es la velocidad de entregar.',
            doctrine: 'La especificación llega de la gestión y la ingeniería la convierte en tests ejecutables, para que la spec se pruebe a sí misma. Rojo, verde, refactor para humanos; para agentes, cada línea de trabajo se verifica antes de contar. La validación se volvió el cuello de botella, lo que hace de la verificación la capacidad real del sistema.',
          },
          {
            title: 'Automatiza el camino a producción',
            line: 'Cada cambio recorre el mismo camino automatizado.',
            doctrine: 'Todos integran al tronco compartido varias veces al día, cada integración compila y testea desde cero, y un build roto detiene la línea. La seguridad entra con el primer commit, los ambientes son código que se reconstruye con un clic, y el comportamiento nuevo sale detrás de toggles, separando deploy de release.',
          },
          {
            title: 'Dirige a los agentes',
            line: 'El humano puede no tocar nunca el código y sigue siendo el maestro.',
            doctrine: 'El agente recibe la spec, los límites y los estándares antes de generar nada, y nada se mergea sin verificar, sin excepción. El pairing se extiende a humano más agente: uno trabaja mientras el otro revisa y piensa adelante. Una ventana de contexto es capacidad real; protégela como cualquier otra, porque la sobrecarga produce mal trabajo también en las máquinas.',
          },
          {
            title: 'Monitorea continuamente',
            line: 'El sistema te cuenta cómo se siente en producción.',
            doctrine: 'La observabilidad cubre analytics, latido y salud, y los agentes son parte del sistema: tasa de verificación, deriva y costo por ítem se vigilan como el resto. El monitoreo observa sistemas, servicios y agentes. Nunca apunta a una persona.',
          },
        ],
      },
    ],
    lexicon: {
      kicker: 'El léxico',
      title: 'Dieciséis palabras en las que piensa el modelo.',
      hint: 'Toca una tarjeta para girarla.',
      terms: [
        { term: 'Jidoka', gloss: 'automatización con toque humano', def: 'El sistema detecta su propia anomalía y se detiene solo, para que ningún defecto fluya adelante. La palabra que da nombre a esta edición.' },
        { term: 'Kaizen', gloss: 'mejora continua', def: 'Muchos pasos pequeños, cada uno un ciclo PDCA, subiendo de la condición actual hacia la visión.' },
        { term: 'Kaikaku', gloss: 'mejora discontinua', def: 'El salto a una curva nueva cuando la vieja se aplana. Con el kaizen, hace la mejora incansable.' },
        { term: 'Muda', gloss: 'desperdicio', def: 'Todo lo que no agrega valor al cliente. Dirigir agentes creó desperdicios nuevos: ventanas de contexto quemadas, retrabajo de agente, salida sin verificar.' },
        { term: 'Muri', gloss: 'sobrecarga', def: 'Las colas explotan mucho antes de la utilización total. Nunca sobrecargues personas, sistemas o ventanas de contexto.' },
        { term: 'Mura', gloss: 'desnivel', def: 'El flujo irregular engendra defectos y demanda de falla. Nivela el flujo, incluso cómo los agentes toman trabajo.' },
        { term: 'Gemba', gloss: 'el lugar real', def: 'Donde el valor se crea de verdad. Ve y mira. El gemba de un agente es el rastro de lo que hizo; reproducirlo es cómo se ve el trabajo de máquina.' },
        { term: 'Andon', gloss: 'el cordón de paro', def: 'Cualquiera puede parar la línea cuando algo está mal. Los agentes también jalan, y la razón queda registrada y visible.' },
        { term: 'Poka-yoke', gloss: 'a prueba de errores', def: 'Construido dentro del proceso, para que el error se vuelva imposible en vez de corregido después.' },
        { term: 'Heijunka', gloss: 'nivelación', def: 'El trabajo llega parejo en vez de en olas destructivas, para personas y para agentes.' },
        { term: 'Hansei', gloss: 'reflexión', def: '¿Qué contribuimos nosotros mismos a la falla? La reflexión viene antes de señalar a cualquier otro.' },
        { term: 'Kentou', gloss: 'estudiar antes de construir', def: 'Diseña la solución, arma el contexto, y solo entonces deja que el trabajo empiece.' },
        { term: 'Yokoten', gloss: 'esparcir a los lados', def: 'Una práctica probada en un lugar sirve a todos los lugares, llevada como playbooks en el vault.' },
        { term: 'Obeya', gloss: 'la sala grande', def: 'Toda la información sobre el trabajo, junta en un solo lugar. La nuestra es virtual y se renderiza del registro.' },
        { term: 'Kamishibai', gloss: 'mini auditorías', def: 'Chequeos cortos y frecuentes de que el estándar sigue vivo. La deriva es silenciosa, así que se mira con ritmo; muchos ya corren como código.' },
        { term: 'Hoshin kanri', gloss: 'gestión de la dirección', def: 'Los objetivos estratégicos dirigen la acción en todos los niveles, alineados por catchball antes de cualquier compromiso.' },
      ],
    },
    manual: {
      kicker: 'El manual de campo',
      title: 'El canon entero, encuadernado.',
      body: 'El libro en curso convierte estas tarjetas en un manual de campo, con las historias de piso detrás de cada práctica. Deja tu email y los primeros capítulos te encuentran antes que nadie.',
      emailPlaceholder: 'tu@empresa.com',
      cta: 'Reservar mi copia',
      sending: 'Reservando...',
      done: 'Estás en la lista. Los primeros capítulos te encontrarán.',
      error: 'No pasó. Intenta de nuevo, o escribe a contact@leanit101.com.',
    },
  },
}

export const getCanonContent = (language) => {
  if (!language) return canonContent['en-US']
  if (language.startsWith('pt')) return canonContent['pt-BR']
  if (language.startsWith('es')) return canonContent.es
  return canonContent['en-US']
}

export default canonContent
